import { DataGridColumn, DataType, SourceType, QueryFilter } from "../../../../models/queries/queries.models";
import { I18N } from "aurelia-i18n";
import { autoinject, observable, BindingEngine, useView } from "aurelia-framework";
import { NotificationType, NotificationClientService } from "../../../../services/basics/notification-client.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import { MenuItem } from "../../../components/menu/menu-item";
import { RightshipConfirmationDialogModel } from "../../../dialogs/rightship-confirmation-dialog/rightship-confirmation-dialog";
import { Claims } from "../../../../constants/authentication/claims";
import { CustomGridPageBase } from "../../common/custom-grid-page.base";
import { MobileHelper } from "../../../../helpers/mobile-helper";
import { ZeroHarmPartnersClientService } from "../../../../services/modules/zeroharm-partners.service";

@autoinject()
@useView('views/pages/settings/zeroharm-partner/zeroharm-partner.html')
export class ZeroharmPartner extends CustomGridPageBase {

    public canDeletePartner: boolean;
    public canUpdatePartner: boolean;
    public canAddPartner: boolean;
    public selectedFilter: { title: string, clear: () => void, apply: () => void };
    public selectedColumnSorting: number[] = [];

    @observable()
    public searchText: string;


    // Get DataGrid columns
    private getColumns(): DataGridColumn[] {
        return [
            //Checkbox
            {
                dataType: DataType.CustomTemplate,
                value: `<check-box checked.bind="getRowItem() && getRowItem().isSelected"
                            changed.delegate="getDataGrid().selectRowInMultipleSelection(getRowItem())">
                        </check-box>`,
                name: "select",
                title: MobileHelper.isMobileDevice() ? "" : `<check-box class="check-box-select-all" checked.two-way="toggleAll"></check-box>`,
                parameter: {
                    item: null
                },
                instancePerRow: true,
                sortOrder: 1,
                columnOrder: -2,
                width: "50px",
                isHidden: false,
                isSortable: false,
                getCellClass: () => {
                    return "selector-checkbox";
                }
            },
            //Name
            {
                sortOrder: 1,
                columnOrder: 1,
                name: 'name',
                dataType: DataType.Default,
                title: 'Partner Name',
                isSortable: true,
            },
            //Sub Cat
            {
                sortOrder: 2,
                columnOrder: 2,
                name: 'subCategory',
                dataType: DataType.Default,
                title: 'Sub Category',
                isSortable: true,
            },
            //url
            {
                sortOrder: 3,
                columnOrder: 3,
                name: 'partnerUrl',
                dataType: DataType.Default,
                title: 'Partner Url',
                readOnly: true,
                isSortable: true,
            },
            //No of vessels
            {
                sortOrder: 4,
                columnOrder: 4,
                name: 'noOfVessels',
                dataType: DataType.Default,
                title: 'No. of vessels',
                readOnly: true,
                isSortable: true,
            },
            //Upsell vessels
            {
                sortOrder: 5,
                columnOrder: 5,
                name: 'upsellVessels',
                dataType: DataType.Default,
                title: 'Upsell vessels',
                readOnly: true,
                isSortable: true,
            },
            {
                sortOrder: 6,
                columnOrder: 6,
                name: 'status',
                dataType: DataType.Default,
                title: 'Status',
                readOnly: true,
                isSortable: true,
            },
            
            //Menu (more)
            {
                columnOrder: 99,
                sortOrder: 99,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: "menu",
                title: "More",
                isSortable: false,
                value: `<div class="actions row justify-content-end align-items-middle">
                            <list-menu hide-menu-border="true" menu-items.bind="menuItems(item)" if.bind="menuItems(item).length > 0">
                                <template replace-part="menu-item">
                                    <div class="row nowrap align-items-middle">
                                        <span class="text">\${menuItem.item.text}</span>
                                    </div>  
                                </template>
                            </list-menu>
                        </div>`,
                parameter: {
                    item: null,
                    menuItems: (item) =>
                    {
                        const menuItems: MenuItem[] = [];
                        if (this.canUpdatePartner) {
                            menuItems.push({isVisible: true, click: () => this.editPartner([item]), item: { text: "Edit" }, classList: ''});
                        }
                        
                        if (this.canDeletePartner) {
                            menuItems.push({isVisible: true, click: () => this.showDeleteDialog([item]), item: { text: "Remove" }, classList: ''});
                        }

                        return menuItems;
                    }
                }
            }
        ];
    }


    activate() {
        this.setItemSource();
    }

    constructor(protected i18N: I18N, protected bindingEngine: BindingEngine, 
        private zeroharmPartnersClientService: ZeroHarmPartnersClientService, 
        private notificationClientService: NotificationClientService,
        private userClaimsService: UserClaimsService,
        private dialogClientService: DialogClientService) {
            super(i18N, 'Partner', bindingEngine)
            this.canDeletePartner = this.userClaimsService.hasPermission(Claims.CanDeleteZeroHarmParner);
            this.canUpdatePartner = this.userClaimsService.hasPermission(Claims.CanUpdateZeroHarmParner);
            this.canAddPartner = this.userClaimsService.hasPermission(Claims.CanAddZeroHarmParner);

    }

    //Update the data-grid's item-source
    private setItemSource() {

        const url = 'odata/ZeroHarmPartners';
        const filters: QueryFilter[] = [];
        this.itemSource = {
            source: url,
            initialFilters: filters,
            sourceType: SourceType.ODataUrl,
            columns: this.getColumns(),
        };
    }

    public sortByMobile(column: DataGridColumn){
        this.dataGridVM.sortingTriggered(column);
    }

    public sortClass(column: DataGridColumn) {
        if (column.isAscending === true) {
            return "Ascending"
        }
        else if (column.isAscending === false) {
            return "Descending"
        }
        else {
            return "";
        }
    }

    editPartner = (item: any) => {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddZeroHarmPartnerDialog, {
            id: item[0].id,
            reloadData: () => this.dataGridVM.reloadData()
        }, true, false, false, 'right');
    }

    addPartner = () => {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddZeroHarmPartnerDialog, {
            id: null,
            reloadData: () => this.dataGridVM.reloadData()
        }, true, false, false, 'right');
    }

    reloadData = () => {
        if (this.dataGridVM) {
            this.dataGridVM.reloadData();
        }
    }

   
    showDeleteDialog = (partners: any) => {
        const model = new RightshipConfirmationDialogModel();
        model.confirm = "Delete";
        model.cancel = this.i18N.tr('cancel-text');
        model.onConfirm = () => this.deletePartner(partners);
        model.confirmClass = 'primary';

        model.messages.push(`This will delete this entry from the database entirely. Are you sure you want to delete ${ partners.map(x=>x.name).join(', ') }?`);

        model.title = 'Delete Zero Harm Innovation Partner'

        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RightshipConfirmationDialog, model, true, true, false);
    }

    deletePartner = (partners: any[]) => {
        return new Promise<void>((resolve) => {
            const names = partners.map(x=>x.name).join(', ');
            this.zeroharmPartnersClientService.delete(partners.map(h=>h.id))
                .then((response) => { 
                    if (response.isSuccess) {
                        this.notificationClientService.show(`Partner ${names} has been removed successfully`, "", NotificationType.Success);
                        this.activate();
                        }
                    else {
                        this.notificationClientService.show(`Error when removing partner ${names}`, "", NotificationType.Error); 
                    }
                }
                );
            
            resolve();
        })
    }

}